/**
 * Project Settings - Custom Hook
 * Handles all state management and API operations
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { usePromptProfiles } from '@/hooks/usePromptProfiles';
import {
  inboundRoutingKeys,
  scriptsListOptions,
  scriptsListQueryKey,
} from '@/lib/query-options';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { logError } from '@/lib/logger';
import { DEFAULT_MIN_CALL_DURATION_SECONDS } from '@aica/core';
import type { UseProjectSettingsReturn } from '../types';

export function useProjectSettings(projectId: string): UseProjectSettingsReturn {
  const { t } = useTranslation(['projects', 'common']);
  const { selectedOrgId } = useRole();
  const { apiClient } = useApiClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);
  /**
   * Whether the form has edits the server has not seen.
   *
   * Tracked from the ACTIONS the UI is given rather than by diffing form
   * state against a snapshot: hydration writes ~15 fields through the raw
   * setters (and `parseSettings` writes more of them from inside a
   * callback), so any snapshot taken afterwards would have to be rebuilt in
   * two places and would drift the first time a field is added. Wrapping the
   * exposed setters means a field is dirty-tracked by construction — a new
   * setter that forgets to go through `touch` fails the same way it would
   * fail to appear in `handleSave`'s payload: visibly, on the screen that
   * uses it.
   */
  const [isDirty, setIsDirty] = useState(false);
  const [projectName, setProjectName] = useState<string>(() =>
    t('common:common.loading')
  );
  const [projectDescription, setProjectDescription] = useState<string>('');
  const [status, setStatus] = useState<'active' | 'paused' | 'archived'>('active');

  // General settings states
  const [budgetLimit, setBudgetLimit] = useState<number | undefined>(undefined);
  const [monthlyRecordLimit, setMonthlyRecordLimit] = useState<number | undefined>(
    undefined
  );

  const [enableAutoAnalysis, setEnableAutoAnalysis] = useState(true);
  const [enableAutoAdherence, setEnableAutoAdherence] = useState(true);
  // 'auto' = report in the language of the conversation. Defaults to 'auto',
  // not a locale: a fabricated UI default gets written to the DB on the first
  // Save, since the form sends every field at once.
  const [analysisLanguage, setAnalysisLanguage] = useState<string>('auto');
  // Язык распознавания. 'auto' = не подсказывать провайдеру, пусть определит сам.
  const [asrLanguage, setAsrLanguage] = useState<string>('auto');
  const [minCallDurationSeconds, setMinCallDurationSeconds] = useState<
    number | undefined
  >(DEFAULT_MIN_CALL_DURATION_SECONDS);
  /** Level 2 significance filter for chats — min real (agent+customer) messages. */
  const [minChatMessages, setMinChatMessages] = useState<number | undefined>(undefined);

  // Provider states - use defaults from catalog (SSOT)
  // ASR/LLM-провайдер и модель здесь больше не живут (issue #532).
  //
  // Конвейер не читает проектных провайдеров с CP-001 — `getAsrSettings` /
  // `getLlmSettings` берут их ТОЛЬКО из платформенных дефолтов, — а показать
  // этот выбор пользователю форма так и не научилась: ни один экран настроек
  // не рендерил selectAsr/selectLlm. Оставалось ровно одно последствие:
  // состояние, инициализированное литералом 'deepgram', уезжало в
  // `settings_json` при КАЖДОМ сохранении, включая правку языка или порога
  // длительности. Так три активных прод-проекта из шести получили закрепление
  // `deepgram/nova-3` (два из них — 2026-08-27, при обычном сохранении
  // настроек), и `estimate-cost`, единственный читатель этих полей, считал им
  // цену вендора, которым записи не обрабатываются.
  const [crmProvider, setCrmProvider] = useState('bitrix24');

  // Prompt profile
  const [selectedPromptProfile, setSelectedPromptProfile] = useState('');
  const [availableProfiles, setAvailableProfiles] = useState<
    Array<{ id: string; name: string; category: string }>
  >([]);
  // Pinned analysis config (migration 059): opt the project out of the org's
  // category-bound auto-selection in favor of its own explicit profile/script.
  const [promptProfilePinned, setPromptProfilePinned] = useState(false);

  const { data: promptProfilesData } = usePromptProfiles({
    enabled: !!selectedOrgId,
  });

  // Script adherence
  const [selectedScript, setSelectedScript] = useState('');
  const initialScriptRef = useRef('');
  const [availableScripts, setAvailableScripts] = useState<
    Array<{ id: string; name: string; category: string; isActive: boolean }>
  >([]);
  const projectQuery = useQuery({
    queryKey: ['projects', 'settings', 'project', selectedOrgId, projectId],
    enabled: !!selectedOrgId && !!projectId,
    staleTime: 60 * 1000,
    queryFn: () => apiClient.projects.getProjectById(projectId),
  });
  const scriptsQuery = useQuery(scriptsListOptions(apiClient, selectedOrgId));
  const projectScriptsQuery = useQuery({
    queryKey: ['projects', 'settings', 'project-scripts', selectedOrgId, projectId],
    enabled: !!selectedOrgId && !!projectId,
    staleTime: 60 * 1000,
    queryFn: () => apiClient.scripts.getProjectScripts(projectId),
  });

  useEffect(() => {
    if (!projectQuery.error) {
      return;
    }

    logError('project_settings.fetch_project_failed', {
      projectId,
      selectedOrgId,
      error:
        projectQuery.error instanceof Error
          ? projectQuery.error.message
          : 'unknown_error',
    });
    setProjectName(t('projects.unknownProject'));
    toast.error(
      t('projects.failedToLoad'),
      projectQuery.error instanceof Error
        ? projectQuery.error.message
        : t('projects.failedToFetchData')
    );
  }, [
    projectId,
    selectedOrgId,
    projectQuery.error,
    projectQuery.errorUpdatedAt,
    toast,
    t,
  ]);

  useEffect(() => {
    if (!promptProfilesData) {
      setAvailableProfiles([]);
      return;
    }

    setAvailableProfiles(
      promptProfilesData.map((profile) => ({
        id: profile.id,
        name: profile.name,
        category: profile.category || 'unknown',
      }))
    );
  }, [promptProfilesData]);

  useEffect(() => {
    if (!scriptsQuery.error) {
      return;
    }

    logError('project_settings.fetch_scripts_failed', {
      selectedOrgId,
      error:
        scriptsQuery.error instanceof Error
          ? scriptsQuery.error.message
          : 'unknown_error',
    });
  }, [selectedOrgId, scriptsQuery.error, scriptsQuery.errorUpdatedAt]);

  useEffect(() => {
    const scriptsData = scriptsQuery.data;
    if (!scriptsData) {
      setAvailableScripts([]);
      return;
    }

    const activeScripts: Array<{
      id: string;
      name: string;
      category: string;
      isActive: boolean;
    }> = [];
    for (const script of scriptsData) {
      if (script.isActive) {
        activeScripts.push({
          id: script.id,
          name: script.name,
          category: script.category,
          isActive: script.isActive,
        });
      }
    }
    setAvailableScripts(activeScripts);
  }, [scriptsQuery.data]);

  useEffect(() => {
    if (!projectScriptsQuery.error) {
      return;
    }

    logError('project_settings.fetch_project_scripts_failed', {
      projectId,
      selectedOrgId,
      error:
        projectScriptsQuery.error instanceof Error
          ? projectScriptsQuery.error.message
          : 'unknown_error',
    });
  }, [
    projectId,
    selectedOrgId,
    projectScriptsQuery.error,
    projectScriptsQuery.errorUpdatedAt,
  ]);

  useEffect(() => {
    initialScriptRef.current = '';
    setSelectedScript('');
  }, [projectId, selectedOrgId]);

  useEffect(() => {
    const assignedScripts = projectScriptsQuery.data?.scripts;
    if (!assignedScripts || initialScriptRef.current) {
      return;
    }

    const defaultScript =
      assignedScripts.find((script) => script.isDefault) || assignedScripts[0];
    if (!defaultScript) {
      return;
    }

    setSelectedScript(defaultScript.scriptId);
    initialScriptRef.current = defaultScript.scriptId;
  }, [projectScriptsQuery.data]);

  const loadingScripts = scriptsQuery.isLoading;

  // Parse settings from JSON
  const parseSettings = useCallback((settings: Record<string, unknown>) => {
    // Update budget and limits
    if (settings.limits && typeof settings.limits === 'object') {
      const limits = settings.limits as Record<string, unknown>;
      if (typeof limits.budget === 'number') {
        setBudgetLimit(limits.budget);
      }
      if (typeof limits.monthlyRecords === 'number') {
        setMonthlyRecordLimit(limits.monthlyRecords);
      }
    }

    // Update features
    if (settings.features && typeof settings.features === 'object') {
      const features = settings.features as Record<string, unknown>;
      if (typeof features.autoAnalysis === 'boolean') {
        setEnableAutoAnalysis(features.autoAnalysis);
      }
      if (typeof features.autoAdherence === 'boolean') {
        setEnableAutoAdherence(features.autoAdherence);
      }
    }

    // Update language
    if (typeof settings.analysisLanguage === 'string') {
      setAnalysisLanguage(settings.analysisLanguage);
    }
    if (typeof settings.asrLanguage === 'string') {
      setAsrLanguage(settings.asrLanguage);
    }

    // Update prompt profile
    if (settings.prompt && typeof settings.prompt === 'object') {
      const prompt = settings.prompt as Record<string, unknown>;
      if (typeof prompt.defaultProfile === 'string') {
        setSelectedPromptProfile(prompt.defaultProfile);
      }
    }

    // Update providers (CRM only — см. комментарий у состояния выше)
    if (settings.providers && typeof settings.providers === 'object') {
      const providers = settings.providers as Record<string, unknown>;
      if (typeof providers.crm === 'string') {
        setCrmProvider(providers.crm);
      }
    }

    // Load min call duration
    if (typeof settings.minCallDurationSeconds === 'number') {
      setMinCallDurationSeconds(settings.minCallDurationSeconds);
    }

    // Load min chat messages (Level 2 significance filter for chats)
    if (typeof settings.minChatMessages === 'number') {
      setMinChatMessages(settings.minChatMessages);
    }

    // settings.alerts is deliberately neither read nor written any more: the
    // enable toggle and low-score threshold it held were saved to
    // projects.settings_json and read by nothing on the server. Whether an
    // alert fires is decided by rules in business_alert_rules
    // (apps/workflows-api/src/workers/alert-evaluation.ts). Old values stay in
    // the JSON untouched — harmless, and cheaper than a migration.

    // NOTE: Legacy routing (ingest_routing) parsing removed.
    // Use Inbound Routing UI (RoutingTab) instead.
    // NOTE: Channel processing lives in ChannelSettingsTab (own query +
    // PUT /:id/channel-settings, camelCase `channelProcessing`) — the old
    // snake_case `channel_processing` key had no readers and is gone.
  }, []);

  // Only populate form state on initial load or projectId change — NOT on background refetch.
  // This prevents overwriting user edits when React Query refetches (window focus, invalidation).
  const settingsLoadedForRef = useRef<string | null>(null);

  useEffect(() => {
    const project = projectQuery.data;
    if (!project) {
      return;
    }

    // Skip if we already loaded settings for this project+org (prevents refetch overwrite)
    const loadKey = `${selectedOrgId}:${projectId}`;
    if (settingsLoadedForRef.current === loadKey) {
      return;
    }
    settingsLoadedForRef.current = loadKey;

    setProjectName(project.name || t('projects.unknownProject'));
    setProjectDescription(project.description || '');
    setStatus((project.status as 'active' | 'paused' | 'archived') || 'active');

    // Load prompt profile ID from project (database field, not JSON)
    if (project.promptProfileId) {
      setSelectedPromptProfile(project.promptProfileId);
    }
    setPromptProfilePinned(project.promptProfilePinned ?? false);

    if (!project.settingsJson) {
      return;
    }

    try {
      const settings = JSON.parse(project.settingsJson) as Record<string, unknown>;
      parseSettings(settings);
    } catch (parseError) {
      logError('project_settings.parse_settings_failed', {
        projectId,
        error: parseError instanceof Error ? parseError.message : 'unknown_error',
      });
    }
  }, [projectId, selectedOrgId, projectQuery.data, parseSettings, t]);

  /**
   * Wraps a setter so calling it marks the form dirty.
   *
   * Deliberately NOT memoized per setter: it is applied once per render in
   * the returned `actions` object, which is itself a fresh object every
   * render already (it always has been), so memoizing here would buy nothing
   * and only hide which setters are tracked.
   */
  const touch = useCallback(
    <A extends unknown[]>(setter: (...args: A) => void) =>
      (...args: A) => {
        setIsDirty(true);
        setter(...args);
      },
    []
  );

  // A different project is a different form — its edits, and its dirty flag,
  // do not carry over. Mirrors the selection reset just above.
  useEffect(() => {
    setIsDirty(false);
  }, [projectId, selectedOrgId]);

  // Auto-update model when provider changes

  // A user-driven selection change to a DIFFERENT profile/script (including
  // clearing to '') drops the corresponding pin — otherwise a pin set for
  // the previous selection silently reattaches to whatever is picked next
  // (the display/save masking below only hides this by truthiness, it
  // doesn't clear the underlying state). Re-selecting the SAME id is a
  // no-op for the pin. The initial-load effect above sets selection and pin
  // together via the raw setState setters, so it is unaffected.
  const handleSelectedPromptProfileChange = useCallback(
    (value: string) => {
      setSelectedPromptProfile(value);
      if (value !== selectedPromptProfile) {
        setPromptProfilePinned(false);
      }
    },
    [selectedPromptProfile]
  );

  const handleSelectedScriptChange = useCallback((value: string) => {
    setSelectedScript(value);
  }, []);

  // NOTE: Legacy routing handlers removed.
  // Use Inbound Routing (org-level) via RoutingTab instead.

  /**
   * The actions object, built once per set of underlying handlers.
   *
   * Memoized because `touch(setX)` mints a NEW function on every call: built
   * inline in the return statement it would hand every consumer a fresh
   * identity on each render, breaking `React.memo`, `useCallback` deps and —
   * worst — turning any `useEffect` keyed on an action into a loop. The raw
   * `useState` setters this wraps are stable, so the wrapped ones must be
   * too. `touch` itself has no deps, and the four wrapped handlers are
   * `useCallback`s, so this recomputes only when one of them genuinely
   * changes.
   */
  const actions = useMemo(
    () => ({
      setProjectName: touch(setProjectName),
      setProjectDescription: touch(setProjectDescription),
      setStatus: touch(setStatus),
      // Raw setter on purpose — see `syncStatus` in types.ts. The Danger Zone
      // has no Save bar, so a false dirty flag there had nowhere to be
      // cleared: it produced a discard dialog on the way out and blocked the
      // navigation of a Delete that succeeded.
      syncStatus: setStatus,
      setBudgetLimit: touch(setBudgetLimit),
      setMonthlyRecordLimit: touch(setMonthlyRecordLimit),
      setEnableAutoAnalysis: touch(setEnableAutoAnalysis),
      setEnableAutoAdherence: touch(setEnableAutoAdherence),
      setAnalysisLanguage: touch(setAnalysisLanguage),
      setAsrLanguage: touch(setAsrLanguage),
      setMinCallDurationSeconds: touch(setMinCallDurationSeconds),
      setMinChatMessages: touch(setMinChatMessages),
      setCrmProvider: touch(setCrmProvider),
      setSelectedPromptProfile: touch(handleSelectedPromptProfileChange),
      setPromptProfilePinned: touch(setPromptProfilePinned),
      setSelectedScript: touch(handleSelectedScriptChange),
    }),
    [touch, handleSelectedPromptProfileChange, handleSelectedScriptChange]
  );

  const handleSave = useCallback(async () => {
    if (!selectedOrgId) {
      toast.error(t('projects.validation.pleaseSelectOrganization'));
      return;
    }

    setIsSaving(true);
    try {
      // Prepare settings JSON
      const settings = {
        // Провайдер CRM — единственный проектный выбор, который система
        // исполняет. ASR/LLM сюда больше не пишутся (issue #532): конвейер
        // берёт их из платформенных дефолтов, а запись этих полей только
        // портила данные — см. комментарий у состояния.
        crmProvider: crmProvider,

        // Language of the analysis output: 'auto' | en | ru | uk | es
        analysisLanguage: analysisLanguage || 'auto',
        // Language hint for the ASR provider: 'auto' or a catalog code.
        asrLanguage: asrLanguage || 'auto',

        // Additional settings (description also stored in project table)
        description: projectDescription,
        prompt: {
          defaultProfile: selectedPromptProfile,
        },
        limits: {
          budget: budgetLimit,
          monthlyRecords: monthlyRecordLimit,
        },
        features: {
          autoAnalysis: enableAutoAnalysis,
          autoAdherence: enableAutoAdherence,
        },
        // NOTE: settings.delivery is intentionally NOT written here. Delivery
        // is managed by the Delivery Rules section (own API); blind-writing
        // constant defaults stomped saved config on every Save (audit B-9).
        // Min call duration (seconds) for short call filtering
        minCallDurationSeconds: minCallDurationSeconds,
        // Min chat messages (Level 2 significance filter) for short chat filtering
        minChatMessages: minChatMessages,
        // NOTE: Legacy ingest_routing removed. Use Inbound Routing (org-level) instead.
      };

      // Script assignment is saved BEFORE the project PATCH below. The order
      // used to matter because the server validated a script pin against the
      // already-persisted assignment; the pin is gone (#707), but the order
      // stays: the compensation path below rolls the assignment back when the
      // PATCH fails, and that only works if the assignment happened first.
      const previousScript = initialScriptRef.current;
      const scriptChanged = selectedScript !== previousScript;
      const assignmentsBeforeSave = projectScriptsQuery.data?.scripts ?? [];
      const previousAssignment = assignmentsBeforeSave.find(
        (assignment) => assignment.scriptId === previousScript
      );
      const selectedAssignment = assignmentsBeforeSave.find(
        (assignment) => assignment.scriptId === selectedScript
      );
      let scriptMutationAttempted = false;
      let previousAssignmentRemoved = false;
      let selectedAssignmentCreated = false;
      let defaultMutationAttempted = false;

      try {
        if (scriptChanged) {
          scriptMutationAttempted = true;
          if (!selectedScript && previousScript) {
            await apiClient.scripts.unassignFromProject(projectId, previousScript);
            previousAssignmentRemoved = true;
          } else if (selectedScript && selectedAssignment) {
            // The target may already be attached as a non-default assignment.
            // Promote it in place instead of POSTing a duplicate unique row.
            defaultMutationAttempted = true;
            await apiClient.scripts.setDefaultScript(projectId, selectedScript);
          } else if (selectedScript) {
            defaultMutationAttempted = true;
            await apiClient.scripts.assignToProject(projectId, selectedScript, true);
            selectedAssignmentCreated = true;
          }
        }

        // Settings JSON and scalar project fields are one PATCH/one project-row
        // update. This avoids the old partial-save state where /settings could
        // succeed and the following project PATCH fail (or vice versa).
        await apiClient.projects.updateProject(projectId, {
          name: projectName,
          description: projectDescription,
          status,
          settings,
          promptProfileId: selectedPromptProfile || null,
          promptProfilePinned: selectedPromptProfile ? promptProfilePinned : false,
        });
      } catch (projectError) {
        // Script assignment is stored in a separate table/API. Compensate it if
        // the atomic project-row PATCH fails so the user never gets a reported
        // failed save with a silently changed default script.
        if (scriptMutationAttempted) {
          const rollbackErrors: string[] = [];

          // Delete only a row this save successfully created. A target that was
          // already assigned before Save must never be removed by compensation.
          if (selectedAssignmentCreated && selectedScript) {
            try {
              await apiClient.scripts.unassignFromProject(projectId, selectedScript);
            } catch (rollbackError) {
              rollbackErrors.push(
                rollbackError instanceof Error ? rollbackError.message : 'unknown_error'
              );
            }
          }

          if (previousAssignment) {
            try {
              let previousStillAssigned = !previousAssignmentRemoved;
              try {
                const currentAssignments =
                  await apiClient.scripts.getProjectScripts(projectId);
                previousStillAssigned = currentAssignments.scripts.some(
                  (assignment) => assignment.scriptId === previousAssignment.scriptId
                );
              } catch (refreshError) {
                rollbackErrors.push(
                  refreshError instanceof Error ? refreshError.message : 'unknown_error'
                );
              }

              if (!previousStillAssigned) {
                await apiClient.scripts.assignToProject(
                  projectId,
                  previousAssignment.scriptId,
                  previousAssignment.isDefault
                );
              } else if (previousAssignment.isDefault && defaultMutationAttempted) {
                await apiClient.scripts.setDefaultScript(
                  projectId,
                  previousAssignment.scriptId
                );
              }
            } catch (rollbackError) {
              rollbackErrors.push(
                rollbackError instanceof Error ? rollbackError.message : 'unknown_error'
              );
            }
          }
          if (rollbackErrors.length > 0) {
            logError('project_settings.script_rollback_failed', {
              projectId,
              previousScriptId: previousScript || null,
              attemptedScriptId: selectedScript || null,
              errors: rollbackErrors,
            });
          }
        }
        throw projectError;
      }

      if (scriptChanged) {
        initialScriptRef.current = selectedScript;
      }
      // Allow next refetch to repopulate form with fresh server data
      settingsLoadedForRef.current = null;
      await Promise.all([projectQuery.refetch(), projectScriptsQuery.refetch()]);

      // The two refetches above only cover THIS screen's own queries. Status,
      // name and description are also what the projects LIST and the project
      // sheet render, and they read different keys — so without this, going
      // back to the list after switching a project to Paused showed it Active
      // until the page was reloaded.
      //
      // By prefix, not by exact key: the list key ends in a params object
      // (`{page,limit,includeArchived,statuses}`) that depends on which filter
      // chip and which page the list happened to be on, and this hook has no
      // way to know that. Every cached page of every filter is stale after a
      // save, which is exactly what a prefix match expresses.
      //
      // The four invalidations are independent, so they run together: awaited
      // one by one, each waited for the previous one's refetch of any ACTIVE
      // query, and the form stayed "saving" for the sum of the round trips.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects', 'list'] }),
        queryClient.invalidateQueries({
          queryKey: ['projects', 'detail', selectedOrgId, projectId],
        }),
        // Список скриптов теперь показывает, каким проектам скрипт назначен, и
        // маркер «Не назначен» у тех, кого не выберет никто (#498). Назначение
        // меняется ЗДЕСЬ, а список живёт своим кешем со `staleTime` 5 минут и
        // без рефетча по фокусу — без этой строки пользователь, назначивший
        // скрипт проекту, ещё пять минут видел бы на нём предупреждение, что
        // скрипт не применяется никогда.
        queryClient.invalidateQueries({
          queryKey: scriptsListQueryKey(selectedOrgId),
        }),
        // Status is saved here too — archiving or restoring a project from this
        // screen moves it in or out of the routing rule count (#1672). The ROOT
        // key, as `useProjectLifecycle` does: the routing chip's list lives
        // under a filter object this hook cannot reconstruct.
        queryClient.invalidateQueries({ queryKey: inboundRoutingKeys.root }),
      ]);

      // Only on the success path: a failed save leaves the edits in the form,
      // so it must leave the warning about them too.
      setIsDirty(false);

      toast.success(
        t('projects.success.settingsSaved'),
        t('projects.success.settingsUpdated')
      );
    } catch (error) {
      // Discard optimistic local form/assignment state and re-read the server
      // after any failed multi-resource save attempt.
      settingsLoadedForRef.current = null;
      initialScriptRef.current = '';
      await Promise.allSettled([projectQuery.refetch(), projectScriptsQuery.refetch()]);
      // Назначение меняется ДО патча проекта, а компенсация при откате сама
      // может не сработать (её отказ логируется как
      // `project_settings.script_rollback_failed`). Значит после неудачного
      // сохранения назначение на сервере может быть уже другим — список
      // скриптов обязан перечитаться и здесь, иначе он ещё пять минут будет
      // показывать бейджи, которых больше нет.
      await queryClient.invalidateQueries({
        queryKey: scriptsListQueryKey(selectedOrgId),
      });
      logError('project_settings.save_failed', {
        projectId,
        selectedOrgId,
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      toast.error(
        t('projects.error.failedToSaveSettings'),
        error instanceof Error ? error.message : t('common:common.errorUnknown')
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedOrgId,
    projectId,
    crmProvider,
    projectDescription,
    selectedPromptProfile,
    promptProfilePinned,
    analysisLanguage,
    asrLanguage,
    budgetLimit,
    monthlyRecordLimit,
    enableAutoAnalysis,
    enableAutoAdherence,
    minCallDurationSeconds,
    minChatMessages,
    selectedScript,
    projectName,
    status,
    apiClient,
    projectQuery,
    projectScriptsQuery,
    queryClient,
    toast,
    t,
  ]);

  return {
    state: {
      projectName,
      projectDescription,
      status,
      budgetLimit,
      monthlyRecordLimit,
      enableAutoAnalysis,
      enableAutoAdherence,
      analysisLanguage,
      asrLanguage,
      minCallDurationSeconds,
      minChatMessages,
      crmProvider,
      selectedPromptProfile,
      availableProfiles,
      promptProfilePinned,
      selectedScript,
      availableScripts,
      loadingScripts,
    },
    actions,
    isSaving,
    isDirty,
    // Save writes every field from form state, and several of those fields
    // persist their empty value. A
    // Save fired before hydration would therefore not be a no-op — it would
    // erase what the project had. On a slow load the button is reachable long
    // before the data lands, so the caller disables it until this is true.
    isLoaded: !projectQuery.isLoading && !!projectQuery.data,
    handleSave,
  };
}
